/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1791763200000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Oct 12 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "sftp",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to SFTP",
          "depth": 0,
          "id": "connect-to-sftp"
        }, {
          "value": "Supported authentication methods",
          "depth": 1,
          "id": "supported-authentication-methods"
        }, {
          "value": "PGP encryption",
          "depth": 1,
          "id": "pgp-encryption"
        }, {
          "value": "Password-protected zip",
          "depth": 1,
          "id": "password-protected-zip"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Select file format",
          "depth": 1,
          "id": "select-file-format"
        }, {
          "value": "Enter filename",
          "depth": 1,
          "id": "enter-filename"
        }, {
          "value": "Set filename offset",
          "depth": 1,
          "id": "set-filename-offset"
        }, {
          "value": "CSV options",
          "depth": 1,
          "id": "csv-options"
        }, {
          "value": "Columns to sync",
          "depth": 1,
          "id": "columns-to-sync"
        }, {
          "value": "Batch size",
          "depth": 1,
          "id": "batch-size"
        }, {
          "value": "Empty file results",
          "depth": 1,
          "id": "empty-file-results"
        }, {
          "value": "Compress output files",
          "depth": 1,
          "id": "compress-output-files"
        }, {
          "value": "Encoding",
          "depth": 0,
          "id": "encoding"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Validate your setup",
          "depth": 1,
          "id": "validate-your-setup"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      ul: "ul",
      li: "li",
      strong: "strong",
      p: "p",
      a: "a",
      code: "code",
      h3: "h3",
      pre: "pre",
      h4: "h4",
      em: "em",
      img: "img",
      span: "span",
      ol: "ol"
    }, _provideComponents(), props.components), {RowAndColumnOrderingInfo, FeatureFlag, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!FeatureFlag) _missingMdxReference("FeatureFlag", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!RowAndColumnOrderingInfo) _missingMdxReference("RowAndColumnOrderingInfo", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Object Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Any data set"
            }), _jsx(_components.td, {
              children: "Sync data from a source to SFTP as CSV, JSON, NDJSON, XML, or Parquet files"
            }), _jsx(_components.td, {
              children: "Insert, All, Diff"
            })]
          })
        })]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "All"
          }), ": All mode creates one file with all the rows in the query results, every time the sync runs."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Insert"
          }), ": Insert mode creates one file with the rows that were added since the last sync."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Diff"
          }), ": Diff mode creates three files, one for rows added, one for rows changed, and another for rows removed since the last sync."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(RowAndColumnOrderingInfo, {
        variant: "file"
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-sftp",
        children: "Connect to SFTP"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "SFTP"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "SFTP"
        }), " by entering the following required fields into Hightouch:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Hostname"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Port"
          })
        }), "\n", _jsxs(_components.li, {
          children: ["Credentials (see the ", _jsx(_components.a, {
            href: "#supported-authentication-methods",
            children: "supported authentication methods"
          }), " below)"]
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["The user needs to have access to the target directory and file with ", _jsx(_components.strong, {
            children: "write\nprivileges"
          }), ". A ", _jsx(_components.code, {
            children: "permission denied"
          }), " error message during a sync indicates the\nuser may not have write permissions for both the directory and the file."]
        })
      }), "\n", _jsx(WhitelistDetails, {
        target: "SFTP server"
      }), "\n", _jsx(_components.h3, {
        id: "supported-authentication-methods",
        children: "Supported authentication methods"
      }), "\n", _jsx(_components.p, {
        children: "We support three authentication methods:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Username + password"
        }), "\n", _jsx(_components.li, {
          children: "Username + SSH private key"
        }), "\n", _jsx(_components.li, {
          children: "Username + SSH private key + passphrase"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "We support two formats for the SSH private key:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "OpenSSH"
        }), "\n", _jsx(_components.li, {
          children: "RSA"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Provide the full private key for the user, including the header and footer."
      }), "\n", _jsx(_components.p, {
        children: "For example:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "-----BEGIN OPENSSH PRIVATE KEY-----\n<private key body>\n<private key body>\n<private key body>\n-----END OPENSSH PRIVATE KEY-----\n"
        })
      }), "\n", _jsx(_components.h4, {
        id: "password-and-ssh-key-together",
        children: "Password and SSH key together"
      }), "\n", _jsx(FeatureFlag, {
        featureName: "Password and SSH key authentication for SFTP"
      }), "\n", _jsxs(_components.p, {
        children: ["Some SFTP servers require more than one authentication method on the same login, such as an SSH key followed by the account password. OpenSSH servers set this up with the ", _jsx(_components.code, {
          children: "AuthenticationMethods"
        }), " setting, for example ", _jsx(_components.code, {
          children: "AuthenticationMethods publickey,password"
        }), ". To connect to one of these servers, enter both the ", _jsx(_components.strong, {
          children: "Password"
        }), " and the ", _jsx(_components.strong, {
          children: "Private Key"
        }), ", plus the ", _jsx(_components.strong, {
          children: "Private Key Passphrase"
        }), " if the key has one."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch completes each step with the method the server asks for next, in whatever order the server requires. If your server collects the password through a keyboard-interactive prompt, which is common on PAM-backed servers (", _jsx(_components.code, {
          children: "publickey,keyboard-interactive"
        }), "), Hightouch answers the prompt with the ", _jsx(_components.strong, {
          children: "Password"
        }), " you entered."]
      }), "\n", _jsx(_components.p, {
        children: "If your server accepts either credential on its own, you can still enter both. Hightouch tries the password first, then the private key."
      }), "\n", _jsx(_components.h3, {
        id: "pgp-encryption",
        children: "PGP encryption"
      }), "\n", _jsxs(_components.p, {
        children: ["The SFTP destination supports PGP encryption. Given a ", _jsx(_components.strong, {
          children: "PGP Public Key"
        }), ", all files transferred to the destination will be encrypted."]
      }), "\n", _jsxs(_components.p, {
        children: ["To use PGP encryption, provide your public key in the ", _jsx(_components.strong, {
          children: "PGP Public Key"
        }), " text area including the header and footer in the following format:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "-----BEGIN PGP PUBLIC KEY BLOCK-----\n\n<PGP public key body>\n<PGP public key body>\n<PGP public key body>\n-----END PGP PUBLIC KEY BLOCK-----\n"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The ", _jsx(_components.strong, {
            children: "PGP Private Key"
          }), " is required to decrypt the files synced to your\ndestination. You don't need to insert this value in Hightouch."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "password-protected-zip",
        children: "Password-protected zip"
      }), "\n", _jsxs(_components.p, {
        children: ["The SFTP destination can encrypt output files at rest with a password-protected AES-256 ", _jsx(_components.code, {
          children: ".zip"
        }), ". Unlike SFTP, which only protects data in transit, this keeps a file encrypted after it's delivered to the destination server."]
      }), "\n", _jsxs(_components.p, {
        children: ["To use it, enter a ", _jsx(_components.strong, {
          children: "Zip password"
        }), " in the destination configuration, then set a sync's ", _jsx(_components.a, {
          href: "#compress-output-files",
          children: "compression format"
        }), " to ", _jsx(_components.strong, {
          children: "Zip"
        }), ". Hightouch encrypts that sync's output with the password, and the recipient extracts it using the same password with any zip tool that supports AES-256 (for example, 7-Zip, WinZip, or PeaZip). Syncs that use a different compression format ignore the password."]
      }), "\n", _jsxs(_components.p, {
        children: ["We recommend adding ", _jsx(_components.code, {
          children: ".zip"
        }), " to your ", _jsx(_components.a, {
          href: "#enter-filename",
          children: "filename"
        }), " in the sync configuration."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "A zip password can't be combined with a PGP public key. Both encrypt files at\nrest, so choose the one that fits your use case."
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your SFTP destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the SFTP destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "select-file-format",
        children: "Select file format"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch supports syncing CSV, JSON, NDJSON, XML, and Parquet files via SFTP."
      }), "\n", _jsx(_components.h3, {
        id: "enter-filename",
        children: "Enter filename"
      }), "\n", _jsxs(_components.p, {
        children: ["The filename field lets you specify the parent directory and the name of the file you want to use for your results.\nYou can include timestamp variables in the filename, surrounding each with ", _jsx(_components.code, {
          children: "{}"
        }), ".\nHightouch supports these timestamp variables:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "YYYY"
          }), ": Represents the full year in four digits."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "YY"
          }), ": The last two digits of the year."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "MM"
          }), ": Two-digit month format (01-12)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "DD"
          }), ": Two-digit day format (01-31)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "HH"
          }), ": Two-digit hour format in 24-hour clock (00-23)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "mm"
          }), ": Two-digit minute format (00-59)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "ss"
          }), ": Two-digit second format (00-59)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "ms"
          }), ": Three-digit millisecond format."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "X"
          }), ": Unix timestamp in seconds."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "x"
          }), ": Unix timestamp in milliseconds."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "All dates and times are UTC."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, you could enter ", _jsx(_components.code, {
          children: "upload/{YYYY}-{MM}-{DD}-{HH}-{mm}-result.json"
        }), " to dynamically include the year, month, date, hour, and minute in each uploaded file. Hightouch would insert each file in the ", _jsx(_components.code, {
          children: "upload"
        }), " directory, which would need to already exist in your bucket."]
      }), "\n", _jsxs(_components.p, {
        children: ["You can also use other ", _jsx(_components.a, {
          href: "/syncs/mapping-data#variable-values",
          children: "variable values"
        }), " to include sync metadata in the filename:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{model.id}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{model.name}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{sync.id}"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "{sync.run.id}"
          })
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "If a file already exists at the path you entered at the time of a sync,\nHightouch overwrites it."
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If you are using an audience and would like to include the audience name, you\nwill still use ", _jsx(_components.code, {
            children: "{model.name}"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "set-filename-offset",
        children: "Set filename offset"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch uses the timestamp of the sync run to fill in timestamp variables. You can optionally include an offset in seconds. For example, if you want the filename's date to be 24 hours ", _jsx(_components.em, {
          children: "before"
        }), " the sync takes place, enter ", _jsx(_components.code, {
          children: "-86400"
        }), " (", _jsx(_components.code, {
          children: "24 hours * 60 minutes * 60 seconds"
        }), "). If you want the filename's data to be one hour after the sync takes place, you would enter ", _jsx(_components.code, {
          children: "3600"
        }), " (", _jsx(_components.code, {
          children: "60 minutes * 60 seconds"
        }), ")."]
      }), "\n", _jsx(_components.h3, {
        id: "csv-options",
        children: "CSV options"
      }), "\n", _jsx(_components.p, {
        children: "If you're syncing to a CSV file, you have additional configuration options:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Delimiter"
          }), ": Your options are comma (", _jsx(_components.code, {
            children: ","
          }), "), semicolon (", _jsx(_components.code, {
            children: ";"
          }), "), pipe (", _jsx(_components.code, {
            children: "|"
          }), "), tilde (", _jsx(_components.code, {
            children: "~"
          }), "), and tab"]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) Whether to ", _jsx(_components.strong, {
            children: "include a CSV header row"
          }), " in the exported files"]
        }), "\n", _jsxs(_components.li, {
          children: ["(Optional) Whether to ", _jsxs(_components.strong, {
            children: ["include a ", _jsx(_components.a, {
              href: "https://en.wikipedia.org/wiki/Byte_order_mark",
              children: "byte order mark (BOM)"
            })]
          }), " in the exported files, the BOM is ", _jsx(_components.code, {
            children: "<U+FEFF>"
          })]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "columns-to-sync",
        children: "Columns to sync"
      }), "\n", _jsx(_components.p, {
        children: "You can export all columns exactly as your model returns them or choose to export specific ones."
      }), "\n", _jsxs(_components.p, {
        children: ["If you need to rename or transform any column values you're syncing, you can use the ", _jsx(_components.a, {
          href: "/syncs/mapping-data#advanced-mapper",
          children: "advanced mapper"
        }), " to do so. If you choose this option, Hightouch only syncs the fields you explicitly map."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-sftp-add-mappings.png",
          alt: "Field mapping in the Hightouch UI"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The preceding example shows how to selectively export the ", _jsx(_components.code, {
          children: "id"
        }), ", ", _jsx(_components.code, {
          children: "email"
        }), " and ", _jsx(_components.code, {
          children: "location"
        }), ". These columns are mapped to new fields in the destination file as ", _jsx(_components.code, {
          children: "user_id"
        }), ", ", _jsx(_components.code, {
          children: "user_email"
        }), " and ", _jsx(_components.code, {
          children: "user_location"
        }), ". Hightouch exports these fields to the new fields in the file and ignores all other columns from your results."]
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: [_jsx(_components.code, {
            children: "Boolean"
          }), " values that appear as ", _jsx(_components.code, {
            children: "True"
          }), " and ", _jsx(_components.code, {
            children: "False"
          }), " in your model appear as ", _jsx(_components.code, {
            children: "1"
          }), "\nand blank respectively in the file created during the sync. If you'd like for\nthese values to appear in another format in your downstream file, you can use\n", _jsx(_components.a, {
            href: "/syncs/mapping-data#template-mapping",
            children: "template mapping"
          }), " to achieve your\ndesired format."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "batch-size",
        children: "Batch size"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, and depending on your ", _jsx(_components.a, {
          href: "#supported-syncing",
          children: "sync mode"
        }), ", Hightouch sends one file for each export. (In diff mode, Hightouch sends three files.)"]
      }), "\n", _jsx(_components.p, {
        children: "By enabling batching, you allow Hightouch to send multiple files if your model query results are large. This can help avoid file size errors."
      }), "\n", _jsx(_components.h3, {
        id: "empty-file-results",
        children: "Empty file results"
      }), "\n", _jsx(_components.p, {
        children: "You can select how Hightouch should handle empty results files. Empty result files can occur if your model's query results haven't changed since the last sync."
      }), "\n", _jsxs(_components.p, {
        children: ["You can select whether to ", _jsx(_components.strong, {
          children: "skip empty files"
        }), ". If you skip empty files, it means Hightouch won't export any files if your model's query results haven't changed since your last sync."]
      }), "\n", _jsx(_components.h3, {
        id: "compress-output-files",
        children: "Compress output files"
      }), "\n", _jsx(_components.p, {
        children: "You can compress each output file. In the sync configuration, set the compression format to one of:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "None"
          }), ": Hightouch delivers the file uncompressed."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Gzip"
          }), ": Hightouch compresses the file with gzip."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Zip"
          }), ": Hightouch packages the file in a ", _jsx(_components.code, {
            children: ".zip"
          }), " archive. If the destination has a ", _jsx(_components.a, {
            href: "#password-protected-zip",
            children: "zip password"
          }), ", the archive is AES-256 encrypted at rest; otherwise it's a plain zip."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["We recommend adding the matching extension (", _jsx(_components.code, {
          children: ".gz"
        }), " or ", _jsx(_components.code, {
          children: ".zip"
        }), ") to your ", _jsx(_components.a, {
          href: "#enter-filename",
          children: "filename"
        }), ", though it isn't required."]
      }), "\n", _jsx(_components.h2, {
        id: "encoding",
        children: "Encoding"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch saves the file in ASCII encoding. If there are any non-ASCII characters in your results, the file will be saved with utf-8 encoding. If you've enabled ", _jsx(_components.a, {
          href: "#csv-options",
          children: "including a BOM"
        }), ", the file will be saved with utf-8 encoding with BOM."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to destinations, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error\ncodes"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Test connection"
        }), " button on the destination configuration page tries to connect to your configured server, but it can only detect whether your credentials are completely invalid. To fully test user permissions, create and run a one-row test sync to verify that your data syncs to your SFTP server."]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "all-configured-authentication-methods-failed",
        children: "All configured authentication methods failed"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-css",
          children: [_jsx(_components.span, {
            className: "hljs-attribute",
            children: "All"
          }), " configured authentication methods failed\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The SFTP server rejected every credential Hightouch tried. Common reasons:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "The username, password, private key, or passphrase is incorrect."
        }), "\n", _jsxs(_components.li, {
          children: ["The server requires a password and an SSH key on the same login. See ", _jsx(_components.a, {
            href: "#password-and-ssh-key-together",
            children: "Password and SSH key together"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "Solution:"
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Test the same credentials with a standalone SFTP client. Running ", _jsx(_components.code, {
            children: "sftp -v"
          }), " shows which authentication methods the server accepts at each step."]
        }), "\n", _jsxs(_components.li, {
          children: ["If the server requires a password and a key together, follow ", _jsx(_components.a, {
            href: "#password-and-ssh-key-together",
            children: "Password and SSH key together"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Otherwise, correct the credentials in the destination configuration. If you're not sure which method your server uses, check with whoever manages your SFTP server."
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "renaming-temporary-file-to-target-file-failed",
        children: "Renaming temporary file to target file failed"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " During an SFTP sync, Hightouch writes data to a temporary file on your server, then renames it to the final filename from your sync configuration. This avoids partial writes if the network drops. The error means a downstream process on the SFTP server consumed or deleted the temporary file before the sync finished, or the ", _jsx(_components.a, {
          href: "#connect-to-sftp",
          children: "credentials you're using"
        }), " lack permission to rename the temporary file or modify its directory."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Adjust directory listeners to ignore temporary files, which are prefixed with a period (", _jsx(_components.code, {
          children: "."
        }), "), or set a different temporary file directory in your sync configuration. Also confirm the SFTP user has permission to rename files in the target directory."]
      }), "\n", _jsx(_components.h4, {
        id: "connection-timed-outwaiting-for-handshake",
        children: "Connection timed out/waiting for handshake"
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Hightouch's IP addresses aren't allowlisted and may be blocked by your SFTP firewall."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Allowlist Hightouch's ", _jsx(_components.a, {
          href: "/security/networking#ip-addresses",
          children: "IP addresses"
        }), " on your SFTP firewall. If you're unsure whether the IPs are allowlisted, ask your network or IT team, or your third-party SFTP vendor, to verify."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
